import React from 'react';

import { COMP_PALETTE } from './ComparableEventsChart';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

const paceLabel = (pct) => {
  const sign = pct >= 0 ? '+' : '−';
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
};

const PaceBadge = ({ aheadBehind }) => {
  if (!aheadBehind) return <span className="mi-ce-table-pace-empty">—</span>;
  return (
    <span className={`mi-ce-table-pace-pill mi-ce-table-pace-pill--${aheadBehind.status}`}>
      {aheadBehind.status === 'on_pace' ? 'On pace' : paceLabel(aheadBehind.pct)}
    </span>
  );
};

const CapacityCell = ({ pct, color }) => (
  <div className="mi-ce-table-cap">
    <span className="mi-ce-table-cap-track">
      <span
        className="mi-ce-table-cap-fill"
        style={{ width: `${Math.max(0, Math.min(pct || 0, 100))}%`, backgroundColor: color }}
      />
    </span>
    <span className="mi-ce-table-cap-pct">{(pct || 0).toLocaleString()}%</span>
  </div>
);

const ComparableEventsTable = ({ table, currentDte, comps, onRemove, busy }) => {
  if (!table || table.length === 0) return null;
  let compIndex = -1;
  const comparisonIdByEventId = new Map((comps || []).map((c) => [c.id, c.comparison_id]));

  return (
    <div className="mi-ce-table-wrap" role="table" aria-label="Comparable events">
      <div className="mi-ce-table-grid mi-ce-table-header" role="row">
        <div role="columnheader">Event</div>
        <div className="mi-ce-table-num" role="columnheader">{`At D-${currentDte ?? 0}`}</div>
        <div className="mi-ce-table-num" role="columnheader">Final</div>
        <div className="mi-ce-table-num" role="columnheader">Capacity</div>
        <div className="mi-ce-table-num" role="columnheader">Pace vs this event</div>
      </div>

      {table.map((row) => {
        const color = row.is_subject
          ? 'var(--sl-brand-600)'
          : COMP_PALETTE[(compIndex += 1) % COMP_PALETTE.length];
        return (
          <div
            key={row.id}
            role="row"
            className={`mi-ce-table-grid mi-ce-table-row${row.is_subject ? ' mi-ce-table-row--subject' : ''}`}
          >
            <div className="mi-ce-table-event" role="cell">
              <span className="mi-ce-table-dot" style={{ backgroundColor: color }} />
              <div className="mi-ce-table-event-text">
                <span className="mi-ce-table-event-name">
                  {row.name}
                  {row.is_subject && <span className="mi-ce-table-event-suffix"> (this event)</span>}
                </span>
                <span className="mi-ce-table-event-meta">
                  {formatDate(row.event_date)}
                  {row.venue_name ? ` · ${row.venue_name}` : ''}
                </span>
              </div>
              {!row.is_subject && onRemove && comparisonIdByEventId.has(row.id) && (
                <button
                  type="button"
                  className="mi-ce-table-remove"
                  disabled={busy}
                  aria-label={`Remove ${row.name}`}
                  onClick={() => onRemove(comparisonIdByEventId.get(row.id))}
                >
                  ×
                </button>
              )}
            </div>
            <div className="mi-ce-table-num mi-ce-table-figure" role="cell">
              {(row.at_current_day || 0).toLocaleString()}
            </div>
            <div className="mi-ce-table-num mi-ce-table-figure" role="cell">
              {row.is_subject ? '—' : (row.final || 0).toLocaleString()}
            </div>
            <div className="mi-ce-table-num" role="cell">
              <CapacityCell pct={row.capacity_pct} color={color} />
            </div>
            <div className="mi-ce-table-num mi-ce-table-pace" role="cell">
              {row.is_subject ? <span className="mi-ce-table-pace-empty">—</span> : <PaceBadge aheadBehind={row.ahead_behind} />}
            </div>
          </div>
        );
      })}

      <div className="mi-ce-table-footnote">
        {`Pace compares each show's sales at the same point in its cycle (D-${currentDte ?? 0}) to this event.`}
      </div>
    </div>
  );
};

export default ComparableEventsTable;
